import { Copy, Check, Send, X, AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';

interface SlideshowItem {
  duration: number;
  payload: {
    Sponsor: string;
  };
}

interface PublishTarget {
  sheetId: string;
  sheetName: string | null;
  appId: number;
  appToken: string;
  isActive: boolean;
  configured: boolean;
  uiOverwriteRisk: boolean;
}

interface PublishPreviewDialogProps {
  templateName: string;
  slideshowItems: SlideshowItem[];
  targets: PublishTarget[];
  onPublish: () => void;
  onCancel: () => void;
  isPublishing: boolean;
}

export default function PublishPreviewDialog({
  templateName,
  slideshowItems,
  targets,
  onPublish,
  onCancel,
  isPublishing,
}: PublishPreviewDialogProps) {
  const [copied, setCopied] = useState(false);

  const jsonString = JSON.stringify(slideshowItems, null, 2);
  const totalDuration = slideshowItems.reduce((sum, item) => sum + item.duration, 0);
  const sheetsWithOverwriteRisk = targets.filter(t => t.uiOverwriteRisk);

  const handleCopy = () => {
    navigator.clipboard.writeText(jsonString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Publish Preview: {templateName}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {slideshowItems.length} asset{slideshowItems.length !== 1 ? 's' : ''} • Total duration: {totalDuration} seconds
              </p>
            </div>
            <button
              onClick={onCancel}
              disabled={isPublishing}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* UI Overwrite Warning */}
        {sheetsWithOverwriteRisk.length > 0 && (
          <div className="px-6 pt-6 pb-0">
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-yellow-900 dark:text-yellow-200 mb-1">
                    UI Overwrite Risk
                  </h3>
                  <p className="text-sm text-yellow-800 dark:text-yellow-300 mb-2">
                    Proceeding will overwrite Ad Script for the following sheet(s):
                  </p>
                  <ul className="text-sm text-yellow-800 dark:text-yellow-300 list-disc list-inside space-y-0.5">
                    {sheetsWithOverwriteRisk.map(target => (
                      <li key={target.sheetId}>
                        {target.sheetName || `Sheet ${target.sheetId}`}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-3 font-medium">
                    ⚠️ After publishing: If any Singular Control App pages are open, refresh them and do not click Update, or they may overwrite this publish.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* JSON Content */}
        <div className="flex-1 overflow-auto p-6 bg-gray-50 dark:bg-gray-900">
          <div className="relative">
            <pre className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-sm font-mono text-gray-900 dark:text-gray-100 overflow-x-auto">
              {jsonString}
            </pre>
            <button
              onClick={handleCopy}
              className="absolute top-3 right-3 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-xs font-medium flex items-center gap-1 transition-colors"
            >
              {copied ? (
                <>
                  <Check className="h-3 w-3" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-3 w-3" />
                  Copy
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              This will be published to all configured Singular.live sheets
            </p>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={onCancel}
                disabled={isPublishing}
                className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </Button>
              <Button
                onClick={onPublish}
                disabled={isPublishing}
                className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPublishing ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Publishing...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Publish to Singular.live
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
